import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { formatRent, formatDate, getPhotoUrl } from '../../utils/format';
import { walkingTime } from '../../utils/distance';
import { useAuth } from '../../context/AuthContext';

export default function SubleaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageIdx, setImageIdx] = useState(0);
  const [leased, setLeased] = useState(false);
  const [saved, setSaved] = useState(false);
  const [messaging, setMessaging] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const res = await api.get(`/subleases/${id}`);
        setListing(res.data);
        setLeased(res.data.leased || false);
      } catch {
        navigate('/subleases');
      }
      setLoading(false);
    }
    load();
  }, [id, navigate]);

  const isOwner = user?.id === listing?.poster_id;
  // Only use photos that are real absolute URLs (Cloudinary). Old /uploads/ paths are gone from Railway's ephemeral disk.
  const photos = (listing?.photos || []).filter(p => p && p.startsWith('http'));

  const handleToggleLeased = async () => {
    try {
      const res = await api.patch(`/subleases/${id}/leased`);
      setLeased(res.data.leased);
    } catch {}
  };

  const handleSave = async () => {
    try {
      if (saved) {
        await api.delete(`/subleases/${id}/save`);
        setSaved(false);
      } else {
        await api.post(`/subleases/${id}/save`);
        setSaved(true);
      }
    } catch {}
  };

  const handleMessage = async () => {
    setMessaging(true);
    try {
      const res = await api.post('/messages/start', {
        other_user_id: listing.poster_id,
        sublease_id: id,
        conversation_type: 'sublease',
      });
      navigate(`/messages/${res.data.id}`);
    } catch {}
    setMessaging(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove this sublease?')) return;
    try {
      await api.delete(`/subleases/${id}`);
      navigate('/subleases');
    } catch {}
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-8 h-8 border-2 border-[#C8A800] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!listing) return null;

  const walk = walkingTime(listing.latitude, listing.longitude);
  const displayRent = Math.ceil(listing.monthly_rent * 1.015);

  return (
    <div className="page-container max-w-4xl mx-auto">
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-[#777777] hover:text-[#1B1B1B] mb-6 transition-colors nav-link text-sm"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
          <path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/>
        </svg>
        Back to subleases
      </button>

      {/* Photo gallery */}
      <div className="relative rounded-2xl overflow-hidden bg-[#F0F0F0] mb-6" style={{ height: 400 }}>
        {photos.length > 0 ? (
          <>
            <img
              src={getPhotoUrl(photos[imageIdx])}
              alt={listing.address}
              className="w-full h-full object-cover"
            />
            {leased && (
              <div className="absolute top-4 right-4 bg-green-600 text-white text-sm font-bold px-4 py-1.5 rounded-full shadow-lg tracking-wide">
                LEASED
              </div>
            )}
            {listing.boosted && (
              <div className="absolute top-4 left-4">
                <span className="badge-gold">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                  Boosted
                </span>
              </div>
            )}
            {photos.length > 1 && (
              <>
                <button
                  onClick={() => setImageIdx(i => (i - 1 + photos.length) % photos.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 backdrop-blur rounded-full flex items-center justify-center shadow hover:bg-white transition-colors"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M15 18l-6-6 6-6"/></svg>
                </button>
                <button
                  onClick={() => setImageIdx(i => (i + 1) % photos.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 backdrop-blur rounded-full flex items-center justify-center shadow hover:bg-white transition-colors"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M9 18l6-6-6-6"/></svg>
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {photos.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setImageIdx(i)}
                      className={`w-2 h-2 rounded-full transition-colors ${i === imageIdx ? 'bg-[#C8A800]' : 'bg-white/60'}`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-[#CCCCCC]">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-16 h-16">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
            <p className="text-sm">No photos available</p>
          </div>
        )}
      </div>

      {/* Thumbnail strip */}
      {photos.length > 1 && (
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {photos.map((p, i) => (
            <button
              key={i}
              onClick={() => setImageIdx(i)}
              className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-colors ${i === imageIdx ? 'border-[#C8A800]' : 'border-transparent'}`}
            >
              <img src={getPhotoUrl(p)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main info */}
        <div className="lg:col-span-2 flex flex-col gap-5">

          {/* Title & address */}
          <div>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="bg-blue-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full tracking-wide">
                    SUBLEASE
                  </span>
                </div>
                <h1 className="text-2xl font-black text-[#1B1B1B] leading-tight">{listing.address}</h1>
                <p className="text-[#777777] mt-1 nav-link">{listing.city}, {listing.state} {listing.zip}</p>
              </div>
              {!isOwner && (
                <button
                  onClick={handleSave}
                  className="w-10 h-10 border border-[#E8E8E8] bg-white rounded-full flex items-center justify-center hover:bg-[#F8F8F8] transition-colors flex-shrink-0 shadow-sm"
                >
                  {saved ? (
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-red-500">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5 text-[#AAAAAA]">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                  )}
                </button>
              )}
            </div>

            {walk && (
              <div className="flex items-center gap-1.5 mt-2 nav-link text-sm text-[#555555]">
                <span>🚶</span>
                <span>{walk} walking to campus</span>
              </div>
            )}
          </div>

          {/* Sublease dates banner */}
          <div className="card p-4 bg-blue-50 border border-blue-100">
            <div className="flex items-center gap-2 text-blue-700">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              <span className="font-semibold text-sm">
                {formatDate(listing.sublease_start)} – {formatDate(listing.sublease_end)}
              </span>
            </div>
          </div>

          {/* Details grid */}
          <div className="card p-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-[#AAAAAA] nav-link mb-1">Bedrooms</p>
              <p className="font-bold text-[#1B1B1B]">{listing.bedrooms} {listing.bedrooms === 1 ? 'Bedroom' : 'Bedrooms'}</p>
            </div>
            {listing.bathrooms && (
              <div>
                <p className="text-xs text-[#AAAAAA] nav-link mb-1">Bathrooms</p>
                <p className="font-bold text-[#1B1B1B]">{listing.bathrooms} {listing.bathrooms === 1 ? 'Bathroom' : 'Bathrooms'}</p>
              </div>
            )}
            {listing.parking && (
              <div>
                <p className="text-xs text-[#AAAAAA] nav-link mb-1">Parking</p>
                <p className="font-bold text-[#1B1B1B] capitalize">{
                  listing.parking === 'reserved' ? 'Reserved Space' :
                  listing.parking === 'driveway' ? 'Driveway' :
                  listing.parking === 'street' ? 'Street Parking' :
                  listing.parking === 'paid' ? 'Paid Parking' :
                  listing.parking === 'none' ? 'No Parking' : listing.parking
                }</p>
              </div>
            )}
          </div>

          {/* Description */}
          {listing.description && (
            <div className="card p-5">
              <h2 className="font-bold text-[#1B1B1B] mb-3">Description</h2>
              <p className="nav-link text-[#555555] leading-relaxed whitespace-pre-line">{listing.description}</p>
            </div>
          )}

          {/* Reason for subleasing */}
          {listing.reason_for_subleasing && (
            <div className="card p-5">
              <h2 className="font-bold text-[#1B1B1B] mb-3">Reason for Subleasing</h2>
              <p className="nav-link text-[#555555] leading-relaxed">{listing.reason_for_subleasing}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="flex flex-col gap-4">
          {/* Pricing card */}
          <div className="card p-5">
            <div className="mb-4">
              <span className="text-3xl font-black text-[#1B1B1B]">{formatRent(displayRent)}</span>
              <p className="text-xs text-[#AAAAAA] nav-link mt-1">Listed at {formatRent(listing.monthly_rent)} + 1.5% service fee</p>
            </div>

            {!isOwner ? (
              <div className="flex flex-col gap-2">
                <button
                  onClick={handleMessage}
                  disabled={messaging}
                  className="btn-primary w-full py-3 text-sm"
                >
                  {messaging ? 'Opening chat…' : 'Message Poster'}
                </button>
                <button
                  onClick={() => {
                    const params = new URLSearchParams({
                      landlord_id: listing.poster_id,
                      sublease_id: id,
                      rent: listing.monthly_rent,
                      property_name: listing.address,
                    });
                    navigate(`/payments?${params.toString()}`);
                  }}
                  className="w-full py-3 text-sm rounded-lg border border-[#C8A800] text-[#C8A800] hover:bg-[#C8A800] hover:text-[#1B1B1B] transition-colors font-medium"
                >
                  💳 Pay Rent
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <button
                  onClick={handleToggleLeased}
                  className={`w-full py-2.5 text-sm rounded-lg border font-medium transition-colors ${leased ? 'border-green-500 bg-green-50 text-green-700 hover:bg-green-100' : 'border-[#DDDDDD] text-[#555555] hover:border-green-400 hover:text-green-600'}`}
                >
                  {leased ? '✓ Marked as Leased' : 'Mark as Leased'}
                </button>
                <button
                  onClick={() => navigate('/subleases')}
                  className="btn-ghost w-full py-2.5 text-sm border border-[#DDDDDD]"
                >
                  Edit Sublease
                </button>
                <button
                  onClick={handleDelete}
                  className="w-full py-2.5 text-sm rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                >
                  Remove Sublease
                </button>
              </div>
            )}
          </div>

          {/* Posted by */}
          <div className="card p-5">
            <h3 className="font-bold text-[#1B1B1B] mb-3 text-sm">Posted by</h3>
            <div className="flex items-center gap-3">
              {listing.poster_avatar ? (
                <img src={getPhotoUrl(listing.poster_avatar)} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#F0F0F0] flex items-center justify-center text-sm font-bold text-[#AAAAAA]">
                  {listing.poster_name?.[0]}
                </div>
              )}
              <div>
                <p className="font-semibold text-[#1B1B1B] text-sm">{listing.poster_name}</p>
                <p className="text-xs text-[#AAAAAA] nav-link">Subleasor</p>
              </div>
            </div>
          </div>

          {/* Listed date */}
          <p className="text-xs text-[#AAAAAA] nav-link text-center">
            Listed {formatDate(listing.created_at)}
          </p>
        </div>
      </div>
    </div>
  );
}
